/**
 * useDashboardStats — shared TPS / latency / active counter.
 *
 * Polls GET /api/luna/dashboard/stats every 2 seconds so every
 * connected client sees the same numbers. The server keeps the
 * values in process memory and advances them on its own 2 s timer.
 *
 * Returns the current snapshot or null while loading.
 */
import { useEffect, useState } from 'react';

export interface DashboardStats {
  tps: number;
  latency: number;
  active: number;
  updatedAt: number;
}

const POLL_INTERVAL_MS = 2000;
const URL = '/api/luna/dashboard/stats';

export function useDashboardStats(): DashboardStats | null {
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const res = await fetch(URL, { credentials: 'omit' });
        if (!res.ok) return;
        const data = (await res.json()) as {
          success: boolean;
          stats?: DashboardStats;
        };
        if (cancelled || !data.success || !data.stats) return;
        setStats(data.stats);
      } catch {
        /* Silent — next tick will retry */
      }
    };

    void fetchStats();
    const id = window.setInterval(fetchStats, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return stats;
}
